import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, LogOut, Settings, Database, BarChart3, Users, UserCheck, Briefcase } from 'lucide-react';
import { adminAuth } from '../services/adminAuth';

const AdminMode: React.FC = () => {
  const navigate = useNavigate();
  const [currentMode, setCurrentMode] = useState<'test' | 'live'>('live'); // Default to live mode

  // Check authentication on component mount
  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }
  }, [navigate]);

  // Load current mode from sessionStorage on component mount
  useEffect(() => {
    const savedMode = sessionStorage.getItem('admin_mode_override');
    if (savedMode === 'test') {
      setCurrentMode('test');
    } else {
      setCurrentMode('live'); // Default to live mode
    }
  }, []);

  const handleModeChange = (mode: 'test' | 'live') => {
    setCurrentMode(mode);
    sessionStorage.setItem('admin_mode_override', mode);
    console.log(`Mode switched to: ${mode}`);
  };

  const handleGoToLanding = () => {
    navigate('/');
  };

  const handleLogout = () => {
    adminAuth.logout();
    navigate('/admin-login');
  };

  const handleGoToProperties = () => {
    navigate('/admin-properties');
  };

  const handleGoToInsights = () => {
    navigate('/admin-insights');
  };

  const handleGoToBuyerOutreach = () => {
    navigate('/admin-buyer-outreach');
  };

  const handleGoToAgentOutreach = () => {
    navigate('/admin-agent-outreach');
  };

  const handleGoToCampaigns = () => {
    navigate('/admin/campaigns');
  };

  // Don't render if not authenticated
  if (!adminAuth.isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Admin Panel
          </h1>
          <button
            onClick={handleLogout}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4 text-center">
            Current Mode: <span className={`font-bold ${currentMode === 'live' ? 'text-red-600' : 'text-green-600'}`}>
              {currentMode.toUpperCase()}
            </span>
          </h2>
        </div>

        <div className="space-y-4 mb-6">
          <button
            onClick={() => handleModeChange('test')}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              currentMode === 'test'
                ? 'bg-green-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Test Mode
          </button>
          
          <button
            onClick={() => handleModeChange('live')}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              currentMode === 'live'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Live Mode
          </button>
        </div>

        {/* Admin Sections */}
        <div className="space-y-3 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Sections</h3>
          
          <button
            onClick={handleGoToCampaigns}
            className="w-full py-3 px-6 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
          >
            <Briefcase className="w-4 h-4" />
            Campaign Management
          </button>
          
          <button
            onClick={handleGoToInsights}
            className="w-full py-3 px-6 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Website Insights
          </button>

          <button
            onClick={handleGoToBuyerOutreach}
            className="w-full py-3 px-6 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <Users className="w-4 h-4" />
            Buyer Outreach
          </button>

          <button
            onClick={handleGoToAgentOutreach}
            className="w-full py-3 px-6 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
          >
            <UserCheck className="w-4 h-4" />
            Agent Outreach
          </button>

          <button
            onClick={handleGoToProperties}
            className="w-full py-3 px-6 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
          >
            <Database className="w-4 h-4" />
            Manage Properties
          </button>
        </div>

        {/* Navigation */}
        <div className="space-y-3">
          <button
            onClick={handleGoToLanding}
            className="w-full py-3 px-6 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            Go to Landing Page
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMode; 